import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Facebook, CheckCircle } from 'lucide-react';

export const ContactSection = ({ onQuoteClick }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Netlify form submission
    const form = e.target;
    const data = new FormData(form);

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data).toString()
      });
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '(774) 696-7152',
      href: 'tel:7746967152',
      action: 'Call Now'
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'rhinoseal@charter.net',
      href: 'mailto:rhinoseal@charter.net',
      action: 'Send Email'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Worcester, MA 01606',
      href: 'https://maps.google.com/?q=Worcester,MA,01606',
      action: 'Get Directions'
    },
    {
      icon: Clock,
      label: 'Hours',
      value: 'Mon-Sat: 7AM - 6PM',
      href: null,
      action: null
    }
  ];

  return (
    <section id="contact" className="py-24 bg-neutral-950 relative">
      {/* Red stripe accent */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-red-600" />

      {/* Rhino watermark - decorative */}
      <div className="absolute bottom-0 right-0 opacity-[0.03] pointer-events-none" aria-hidden="true">
        <img src="/images/logo.jpg" alt="" role="presentation" className="w-[500px] h-[500px] object-contain" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section header with logo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src="/images/logo.jpg" alt="Rhino Seal" className="w-20 h-20 rounded-full border-4 border-red-600" />
          </div>
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">
            Get In Touch
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-4 mb-6">
            REQUEST YOUR
            <span className="text-red-500"> FREE QUOTE</span>
          </h2>
          <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
            Ready to protect your pavement investment? Contact us today for a free, 
            no-obligation estimate. We serve all of Worcester County!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Contact info cards */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {contactInfo.map((info) => (
                <motion.div
                  key={info.label}
                  className="bg-neutral-900 border border-gray-800 p-4 rounded-sm"
                  whileHover={{ scale: 1.02, borderColor: '#dc2626', boxShadow: '0 0 15px rgba(220, 38, 38, 0.2)' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-red-600/10 border border-red-600/30 rounded-sm flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-neutral-500 text-sm">{info.label}</p>
                      {info.href ? (
                        <motion.a
                          href={info.href}
                          className="text-white font-semibold hover:text-red-500 transition-colors"
                          target={info.href.startsWith('http') ? '_blank' : undefined}
                          rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          data-testid={`contact-${info.label.toLowerCase()}`}
                          whileHover={{ x: 3 }}
                        >
                          {info.value}
                        </motion.a>
                      ) : (
                        <span className="text-white font-semibold">{info.value}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Form - Netlify Compatible */}
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-500/10 border-2 border-green-500 rounded-sm p-8 text-center"
              >
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                <p className="text-neutral-400 mb-6">
                  Thank you for contacting Rhino Seal. We'll get back to you within 24 hours.
                </p>
                <motion.button
                  onClick={() => setIsSubmitted(false)}
                  className="border-2 border-gray-500 text-white px-6 py-3 rounded-sm font-bold uppercase"
                  whileHover={{ scale: 1.05, borderColor: '#dc2626' }}
                >
                  Send Another Message
                </motion.button>
              </motion.div>
            ) : (
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="bg-neutral-900 border border-gray-800 p-8 rounded-sm"
              >
                {/* Netlify hidden fields */}
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>
                    Don't fill this out: <input name="bot-field" />
                  </label>
                </p>

                <div className="flex items-center gap-3 mb-6">
                  <img src="/images/logo.jpg" alt="Rhino" className="w-8 h-8 rounded-full border border-red-600" />
                  <h3 className="text-xl font-bold text-white">Send Us a Message</h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-neutral-400 mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-neutral-800 border-2 border-gray-700 focus:border-red-500 text-white rounded-sm h-12 px-4"
                      placeholder="John Smith"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-neutral-400 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-neutral-800 border-2 border-gray-700 focus:border-red-500 text-white rounded-sm h-12 px-4"
                      placeholder="(555) 123-4567"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-neutral-400 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-neutral-800 border-2 border-gray-700 focus:border-red-500 text-white rounded-sm h-12 px-4"
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-neutral-400 mb-2">Service Needed</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full bg-neutral-800 border-2 border-gray-700 focus:border-red-500 text-white rounded-sm h-12 px-4"
                    data-testid="contact-service"
                  >
                    <option value="">Select a service...</option>
                    <option value="sealcoating">Sealcoating</option>
                    <option value="crackfilling">Crack Filling</option>
                    <option value="both">Sealcoating + Crack Filling</option>
                    <option value="commercial">Commercial Project</option>
                    <option value="other">Other / Not Sure</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-neutral-400 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full bg-neutral-800 border-2 border-gray-700 focus:border-red-500 text-white rounded-sm p-4 resize-none"
                    placeholder="Tell us about your project..."
                    data-testid="contact-message"
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider py-4 rounded-sm flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </motion.button>
              </form>
            )}

            {/* Social links */}
            <div className="mt-6 flex items-center justify-center gap-4">
              <motion.a
                href="https://www.facebook.com/sealcoatingcentralmass/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
                data-testid="contact-facebook"
                whileHover={{ scale: 1.05 }}
              >
                <Facebook className="w-5 h-5" />
                Follow Us on Facebook
              </motion.a>
            </div>
          </motion.div>

          {/* Right Column - Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Google Map - Wider area covering Central MA and Metro West Boston */}
            <div className="bg-neutral-900 border border-gray-800 rounded-sm overflow-hidden">
              <div className="p-4 border-b border-gray-800 flex items-center gap-3">
                <img src="/images/logo.jpg" alt="Rhino" className="w-8 h-8 rounded-full border border-red-600" />
                <div>
                  <h3 className="font-bold text-white">Service Area</h3>
                  <p className="text-sm text-neutral-400">Central Massachusetts & Metro West Boston</p>
                </div>
              </div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d376746.6853723!2d-71.80!3d42.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e37758c4f27d23%3A0x7e7e5b0e7e0c7e7e!2sCentral%20Massachusetts!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus&zoom=9"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Rhino Seal Service Area - Central MA & Metro West Boston"
              />
              <div className="p-4 bg-neutral-800/50">
                <p className="text-sm text-neutral-400 text-center">
                  <span className="text-red-500 font-semibold">Serving:</span> Worcester, Framingham, Natick, Wellesley, Newton, Marlborough, Sudbury, Wayland, Ashland, Shrewsbury, Auburn, Grafton, Millbury, Westborough, Southborough, and surrounding communities
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
